import { createTheme, alpha } from "@mui/material/styles";

const defaultTheme = createTheme();

// Fruitify brand colours. Repeated values intentionally avoid inventing
// unapproved shades while preserving the numeric access used by older views.
export const brand = {
  50: "#F5F3F0",
  100: "#B1D9AE",
  200: "#B1D9AE",
  300: "#52C3BE",
  400: "#52C3BE",
  500: "#52C3BE",
  600: "#52C3BE",
  700: "#572C88",
  800: "#201E5A",
  900: "#201E5A",
};

// Grey scale
export const gray = {
  50: "#F5F3F0",
  100: "#F5F3F0",
  200: "#D5D2CC",
  300: "#D5D2CC",
  400: "#8A837A",
  500: "#8A837A",
  600: "#635E57",
  700: "#3B3A36",
  800: "#3B3A36",
  900: "#2E2E2E",
  1000: "#201E5A",
};

// Green
export const green = {
  50: "#EEFCF3",
  100: "#E0F5E8",
  200: "#BDEAD0",
  300: "#A3E1BF",
  400: "#2FB66D",
  500: "#1C874D",
  600: "#297F50",
  700: "#21693F",
  800: "#0A522C",
  900: "#003D1E",
};

// Red
export const red = {
  50: "#FFEBEB",
  100: "#FFD8D9",
  200: "#FFB3B4",
  300: "#F58E92",
  400: "#EC515D",
  500: "#DF2A38",
  600: "#C31D2A",
  700: "#891E27",
  800: "#611A20",
  900: "#3B1216",
};

// Orange (unchanged from old theme)
export const orange = {
  50: "hsl(45, 100%, 97%)",
  100: "hsl(45, 92%, 90%)",
  200: "hsl(45, 94%, 80%)",
  300: "hsl(45, 90%, 65%)",
  400: "hsl(45, 90%, 40%)",
  500: "hsl(45, 90%, 35%)",
  600: "hsl(45, 91%, 25%)",
  700: "hsl(45, 94%, 20%)",
  800: "hsl(45, 95%, 16%)",
  900: "hsl(45, 93%, 12%)",
};

// Cyan
export const cyan = {
  50: "#ECF4FE",
  75: "#E5F1FF",
  100: "#CCE0FF",
  200: "#A6C8FC",
  300: "#95BDFE",
  400: "#75A7FD",
  500: "#4789FA",
  600: "#0550D1",
  700: "#003A9E",
  800: "#002B75",
  900: "#001E52",
  1000: "#001333",
};

// Indigo
export const indigo = {
  50: "#F5F3F0",
  100: "#B1D9AE",
  200: "#572C88",
  300: "#201E5A",
};

// Cobalt Blue
export const cobaltBlue = {
  500: "#201e5a", // Primary cobalt
};

// Charcoal
export const charcoal = {
  500: "#2E2E2E",
};

// White
export const white = {
  500: "#ffffff",
};

export const yellow = {
  500: "#ffb400",
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[100],
        main: brand[500],
        dark: indigo[200],
        contrastText: charcoal[500],
      },
      secondary: {
        light: brand[100],
        main: indigo[200],
        dark: cobaltBlue[500],
        contrastText: white[500],
      },
      info: {
        light: cyan[100],
        main: cyan[500],
        dark: cyan[600],
        contrastText: gray[50],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
      },
      highlight: brand[500],
      grey: {
        ...gray,
      },
      cyan,
      indigo,
      cobaltBlue,
      charcoal,
      divider: gray[200],
      background: {
        default: gray[50],
        paper: white[500],
        paperHighlight: gray[200],
      },
      rating: {
        default: yellow[50],
      },
      text: {
        primary: charcoal[500],
        secondary: gray[600],
        warning: orange[400],
      },
      action: {
        hover: alpha(brand[500], 0.09),
        selected: alpha(brand[500], 0.14),
      },
      baseShadow:
        "rgba(32, 30, 90, 0.08) 0px 4px 16px, rgba(32, 30, 90, 0.06) 0px 10px 28px -8px",
    },
  },
};

export const typography = {
  fontFamily: '"Poppins", "Helvetica Neue", Arial, sans-serif',
  h1: {
    fontSize: defaultTheme.typography.pxToRem(40),
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(34),
    fontWeight: 700,
    lineHeight: 1.2,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(28),
    fontWeight: 700,
    lineHeight: 1.15,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
    lineHeight: 1.3,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(16),
    fontWeight: 400,
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
    lineHeight: 1.5,
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
    lineHeight: 1.4,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 10,
};

const defaultShadows = [
  "none",
  "var(--template-palette-baseShadow)",
  ...defaultTheme.shadows.slice(2),
];

export const shadows = defaultShadows;
